import axios from "axios";
import type { AxiosInstance } from "axios";

export interface KycVerification {
  id: string;
  userId: string;
  applicantId: string;
  externalUserId: string;
  inspectionId?: string;
  levelName: string;
  status: string;
  reviewStatus?: string;
  reviewResult?: {
    reviewAnswer: string;
  };
  reviewAnswer?: string;
  rejectLabels?: string[] | null;
  applicantType?: string | null;
  correlationId?: string;
  sandboxMode?: boolean;
  clientId?: string;
  applicantActionId?: string | null;
  externalApplicantActionId?: string | null;
  compositionMediaId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface KycAccessTokenResponse {
  success: boolean;
  data: {
    token: string;
    applicantId: string;
    expiresIn: number;
  };
}

export interface KycStatusResponse {
  success: boolean;
  data: KycVerification;
}

export interface CreateKycRequest {
  levelName: string;
}

export interface AccessTokenRequest {
  ttlInSecs?: number;
}

const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";

// Configuração do axios com CORS e credentials
const axiosInstance: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// Interceptor de requisição para garantir que os cookies sejam enviados
axiosInstance.interceptors.request.use(
  (config) => {
    config.withCredentials = true;

    if (typeof document !== "undefined" && import.meta.env.DEV) {
      const cookies = document.cookie;
      console.log("[kycApi] Cookies disponíveis:", cookies);
      console.log("[kycApi] URL da requisição:", config.url);
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Interceptor de resposta para debug
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response) {
      console.error("[kycApi] Erro na resposta:", {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
        headers: error.response.headers,
      });
    }
    return Promise.reject(error);
  },
);

export async function getKycStatus(
  levelName: string,
): Promise<KycStatusResponse> {
  try {
    const response = await axiosInstance.get<KycStatusResponse>(
      `/kyc/status/${levelName}`,
    );
    return response.data;
  } catch (error: any) {
    if (error.response) {
      const status = error.response.status;
      const statusText = error.response.statusText;
      const errorMessage = error.response.data?.message ||
        error.response.data || statusText;
      throw new Error(
        `Erro ao buscar status KYC: ${status} ${statusText} - ${errorMessage}`,
      );
    } else if (error.request) {
      throw new Error(
        "Erro de rede ao buscar status KYC. Verifique sua conexão.",
      );
    } else {
      throw new Error(`Erro ao buscar status KYC: ${error.message}`);
    }
  }
}

export async function createKyc(
  levelName: string = "id-and-liveness",
): Promise<KycStatusResponse> {
  try {
    const response = await axiosInstance.post<KycStatusResponse>("/kyc", {
      levelName,
    });
    return response.data;
  } catch (error: any) {
    if (error.response) {
      const status = error.response.status;
      const statusText = error.response.statusText;
      const errorMessage = error.response.data?.message ||
        error.response.data || statusText;

      // Tratamento especial para erro 429 (já existe processo em andamento)
      if (status === 429) {
        throw new Error(
          "Você já iniciou um processo de verificação KYC. Por favor, continue com o processo existente.",
        );
      }

      throw new Error(
        `Erro ao criar KYC: ${status} ${statusText} - ${errorMessage}`,
      );
    } else if (error.request) {
      throw new Error("Erro de rede ao criar KYC. Verifique sua conexão.");
    } else {
      throw new Error(`Erro ao criar KYC: ${error.message}`);
    }
  }
}

export async function getAccessToken(
  levelName: string,
  ttlInSecs: number = 1440,
): Promise<KycAccessTokenResponse> {
  try {
    const response = await axiosInstance.post<KycAccessTokenResponse>(
      `/kyc/access-token/${levelName}`,
      {
        ttlInSecs,
      },
    );
    return response.data;
  } catch (error: any) {
    if (error.response) {
      const status = error.response.status;
      const statusText = error.response.statusText;
      const errorMessage = error.response.data?.message ||
        error.response.data || statusText;
      throw new Error(
        `Erro ao gerar access token: ${status} ${statusText} - ${errorMessage}`,
      );
    } else if (error.request) {
      throw new Error(
        "Erro de rede ao gerar access token. Verifique sua conexão.",
      );
    } else {
      throw new Error(`Erro ao gerar access token: ${error.message}`);
    }
  }
}
